import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TelegramNotificationBanner from '../components/common/TelegramNotificationBanner';

const HomePage = () => {
  const { user } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-slide pour le hero carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const heroSlides = [
    {
      title: "AGRICULTURE DURABLE",
      subtitle: "Découvrez l'excellence agricole du Bénin",
      description: "Des produits frais, cultivés avec passion par nos agriculteurs locaux",
      image: "https://images.unsplash.com/photo-1574943320219-553eb213f72d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" // Champ agricole
    },
    {
      title: "COMMERCE ÉQUITABLE", 
      subtitle: "Nous connectons producteurs et consommateurs",
      description: "Éliminer les intermédiaires pour des prix justes et des produits de qualité",
      image: "https://images.unsplash.com/photo-1595273670150-bd0c3c392e46?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" // Agriculteurs au travail
    },
    {
      title: "INNOVATION AGRICOLE",
      subtitle: "L'avenir de l'agriculture béninoise",
      description: "Technologie moderne au service de l'agriculture traditionnelle",
      image: "https://images.unsplash.com/photo-1516253593875-bd7ba052fbc2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" // Plantation moderne
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Bannière Telegram */}
      {user && (
        <div className="container mx-auto px-4 mb-6">
          <TelegramNotificationBanner variant="full" />
        </div>
      )}

      {/* Hero Section Carousel */}
      <section className="relative h-screen overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-transform duration-1000 ease-in-out ${
              index === currentSlide ? 'translate-x-0' : 
              index < currentSlide ? '-translate-x-full' : 'translate-x-full'
            }`}
          >
            <div 
              className="h-full bg-cover bg-center bg-no-repeat relative"
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="absolute inset-0 bg-black bg-opacity-50"></div>
              <div className="relative h-full flex items-center justify-center">
                <div className="text-center text-white max-w-4xl mx-auto px-4">
                  <div className="mb-4">
                    <span className="inline-block bg-terracotta text-white px-4 py-2 rounded-full text-sm font-medium uppercase tracking-wider">
                      {slide.title}
                    </span>
                  </div>
                  <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                    {slide.subtitle}
                  </h1>
                  <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto font-light">
                    {slide.description}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link 
                      to="/products" 
                      className="bg-terracotta hover:bg-opacity-90 text-white px-8 py-4 rounded-lg font-medium text-lg transition-all duration-300 transform hover:scale-105"
                    >
                      Découvrir nos produits
                    </Link>
                    <Link 
                      to="/register" 
                      className="border-2 border-white text-white hover:bg-white hover:text-slate px-8 py-4 rounded-lg font-medium text-lg transition-all duration-300"
                    >
                      Devenir producteur
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {/* Navigation dots */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-terracotta scale-125' : 'bg-white bg-opacity-50'
              }`}
            />
          ))}
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Section Mission */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-slate mb-8">
              Bienvenue chez BeninAgri
            </h2>
            <p className="text-xl text-gray-600 mb-12 leading-relaxed">
              Un pionnier où l'innovation se marie avec la tradition pour révolutionner 
              l'agriculture au Bénin. Nous connectons directement les producteurs locaux 
              avec les consommateurs pour un commerce plus équitable et durable.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/products" 
                className="bg-olive hover:bg-terracotta text-white px-8 py-3 rounded-lg font-medium transition-colors duration-300"
              >
                PRODUITS
              </Link>
              {/* <Link 
                to="/farmer/dashboard" 
                className="bg-terracotta hover:bg-olive text-white px-8 py-3 rounded-lg font-medium transition-colors duration-300"
              >
                ESPACE FARMER
              </Link>
              <Link 
                to="/customer/dashboard" 
                className="bg-slate hover:bg-gray-700 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-300"
              >
                ESPACE CLIENT
              </Link> */}
            </div>
          </div>
        </div>
      </section>

      {/* Section Storytelling */}
      <section className="relative py-24">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{ 
            backgroundImage: "url(https://images.unsplash.com/photo-1559827260-dc66d52bef19?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80)" 
          }}
        >
          <div className="absolute inset-0 bg-olive bg-opacity-85"></div>
        </div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-8">
              Nous rationalisons l'approvisionnement local
            </h2>
            <p className="text-xl md:text-2xl font-light leading-relaxed">
              En connectant directement les agriculteurs béninois avec les consommateurs du monde entier, 
              nous créons une chaîne d'approvisionnement transparente, équitable et durable qui profite à tous.
            </p>
          </div>
        </div>
      </section>

      {/* Section Impact */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate mb-6">Notre Impact</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Depuis notre lancement, nous transformons l'agriculture béninoise
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-terracotta mb-3">500+</div>
              <div className="text-gray-600 uppercase tracking-wide">Agriculteurs partenaires</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-olive mb-3">10k+</div>
              <div className="text-gray-600 uppercase tracking-wide">Produits vendus</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-slate mb-3">15</div>
              <div className="text-gray-600 uppercase tracking-wide">Communes couvertes</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-terracotta mb-3">98%</div>
              <div className="text-gray-600 uppercase tracking-wide">Satisfaction client</div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Produits Vedettes */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate mb-6">Nos Spécialités</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez la richesse agricole du Bénin à travers nos produits d'exception
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Ananas */}
            <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="h-80 bg-cover bg-center" 
                   style={{ backgroundImage: "url(https://images.unsplash.com/photo-1589820296156-2454bb8a6ad1?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80)" }}>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-70"></div>
                <div className="absolute bottom-6 left-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Ananas Sucrés</h3>
                  <p className="text-gray-200">Les plus juteux du Bénin</p>
                </div>
              </div>
            </div>

            {/* Maïs */}
            <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="h-80 bg-cover bg-center" 
                   style={{ backgroundImage: "url(https://images.unsplash.com/photo-1551754655-cd27e38d2076?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80)" }}>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-70"></div>
                <div className="absolute bottom-6 left-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Maïs Local</h3>
                  <p className="text-gray-200">Cultivé traditionnellement</p>
                </div>
              </div>
            </div>

            {/* Légumes */}
            <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="h-80 bg-cover bg-center" 
                   style={{ backgroundImage: "url(https://images.unsplash.com/photo-1592924357228-91a4daadcfea?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80)" }}>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-70"></div>
                <div className="absolute bottom-6 left-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Légumes Frais</h3>
                  <p className="text-gray-200">Récoltés quotidiennement</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/products" 
              className="inline-flex items-center bg-terracotta hover:bg-olive text-white px-8 py-4 rounded-lg font-medium text-lg transition-colors duration-300"
            >
              Voir tous nos produits
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Section Valeurs */}
      <section className="py-20 bg-slate text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Nos Valeurs</h2>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              Ce qui nous guide dans notre mission quotidienne
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-terracotta rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">Passion</h3>
              <p className="opacity-90 leading-relaxed">
                Nous sommes passionnés par l'agriculture béninoise et fiers de soutenir nos producteurs locaux.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-olive rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">Qualité</h3>
              <p className="opacity-90 leading-relaxed">
                Nous garantissons des produits de la plus haute qualité, fraîchement récoltés et soigneusement sélectionnés.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-terracotta rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">Communauté</h3>
              <p className="opacity-90 leading-relaxed">
                Nous créons des liens durables entre producteurs et consommateurs pour une communauté agricole forte.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Final */}
      <section className="py-24 bg-gradient-to-r from-terracotta to-olive text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">
            Rejoignez la révolution agricole béninoise
          </h2>
          <p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto font-light">
            Que vous soyez producteur ou consommateur, ensemble nous construisons 
            un avenir plus durable pour l'agriculture au Bénin.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link 
              to="/register" 
              className="bg-white text-slate hover:bg-gray-100 px-10 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105"
            >
              Commencer maintenant
            </Link>
            <Link 
              to="/products" 
              className="border-2 border-white text-white hover:bg-white hover:text-slate px-10 py-4 rounded-lg font-bold text-lg transition-all duration-300"
            >
              Explorer les produits
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;